<template>
  <div
    class="mini-cart"
    :class="{
      'mini-cart--opened': isMiniCartShowing && orderIsInitialized,
    }"
    :style="transformStyle"
    @mouseenter="$store.dispatch('nav/setAdditionalNav', 'mini-cart')"
  >
    <!-- Flash message -->
    <FlashMessages :context="flashContext" class="mini-cart__flash"></FlashMessages>
    <!-- Header -->
    <div class="mini-cart__title">
      <div class="mini-cart__cart-items">
        <span class="icon-bag"></span>
        <span class="mini-cart__cart-items-counter">{{ itemsCount }}</span>
      </div>
      <span>{{ $t('minicart.title') }}</span>
      <span class="mini-cart__close icon-cross" @click="closeAdditionalNav"></span>
    </div>
    <!-- LoadingIndicator -->
    <div class="mini-cart__loader" :class="{ 'mini-cart__loader--loading': cartItemIsLoading }">
      <LoadingIndicator class="mini-cart__loader-image" />
    </div>
    <!-- Empty cart container  -->
    <transition name="toggle-fade">
      <div
        v-show="!itemsLength && !quickAdding"
        ref="emptyContainer"
        class="mini-cart__container mini-cart__container--empty"
        @click="$store.dispatch('nav/setAdditionalNav', '')"
      >
        <EmptyCart :homepageUrl="homepageUrl" />
        <div ref="deliveryNotice" class="mini-cart__delivery-notice">
          <div class="mini-cart__delivery-icon icon-course"></div>
          {{ deliveryNotice }}
        </div>
      </div>
    </transition>
    <!-- Cart container with items -->
    <transition name="toggle-fade">
      <div
        v-show="itemsLength"
        ref="itemsContainer"
        class="mini-cart__container mini-cart__container--filled"
      >
        <!-- NoticeMessage -->
        <NoticeMessage context="mini-cart" class="mini-cart__notice"></NoticeMessage>
        <!-- Cart items -->
        <div ref="cartItems" class="mini-cart__items">
          <OrderItem
            v-for="(item, index) of items"
            :id="Number(item.variantId)"
            :key="item.variantId"
            :class="{
              'order-item--first': index === 0,
              'order-item--transitioning': itemIsGettingAdded,
              'order-item--merging': mergedItemIndex === index,
            }"
            :item="item"
            :index="index"
            :quantity="item.quantity"
            :stock="item.stock"
            :onCartContentChange="onCartContentChange"
            :flashContext="flashContext"
          />
        </div>
        <!-- Promocode component -->
        <PromoCode
          v-if="!adjustmentIsLoading"
          ref="promoCode"
          :flashContext="flashContext"
          class="promo-code--mini-cart"
        ></PromoCode>
        <!-- Total cost -->
        <div class="mini-cart__total-box">
          <div v-if="adjustmentIsLoading" class="mini-cart__loader mini-cart__loader--loading">
            <LoadingIndicator class="mini-cart__loader-image" />
          </div>
          <div v-else>
            <OrderTotals
              :rows="[
                { key: ORDER_TOTALS_ROWS.shipping, value: shippingTotal },
                { key: ORDER_TOTALS_ROWS.total, value: total },
              ]"
            />
          </div>
        </div>
        <!-- Delivery info -->
        <div v-if="!isPreorder" ref="deliveryNotice" class="mini-cart__delivery-notice">
          <div class="mini-cart__delivery-icon icon-course"></div>
          {{ deliveryNotice }}
        </div>
        <!-- Recommendation (inserted & initialised by DY) -->
        <div v-if="!isPreorder" class="js-vue-minicart-recommendation"></div>
      </div>
    </transition>
    <!-- Bottom box -->
    <div v-show="isBottomBoxShowing" class="mini-cart__bottom-box">
      <!-- Checkout Buttons -->
      <div class="mini-cart__checkout">
        <ApplePayExpressCheckout
          v-if="showApplePayButton"
          :isMiniCart="true"
          class="mini-cart__checkout-applepay"
        />
        <Button
          :href="homepageUrl + '/cart'"
          tag="a"
          :isSubmitting="isSubmitting"
          class="mini-cart__cta"
          @click.prevent="redirectToCheckout"
          >{{ $t('global.checkout') }}</Button
        >
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { onLoad } from '@/helpers/event'
import cartMixin from '@/mixins/cart'
import applePayExpressCheckoutMixin from '@/mixins/init-apple-pay-express-checkout'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'
import Button from '@/components/_shared/Button.vue'
import FlashMessages from '@/components/_shared/FlashMessage/FlashMessages.vue'
import NoticeMessage from '@/components/_shared/NoticeMessage/NoticeMessage.vue'
import OrderItem from '@/components/_shared/Order/Item/OrderItem.vue'
import OrderTotals, { ORDER_TOTALS_ROWS } from '@/components/_shared/Order/Totals/OrderTotals.vue'
import PromoCode from './PromoCode/PromoCode.vue'
import ApplePayExpressCheckout from '@/components/Checkout/Payment/ApplePay/ExpressCheckout.vue'
import EmptyCart from './EmptyCart/EmptyCart.vue'

export default {
  name: 'MiniCart',
  components: {
    LoadingIndicator,
    Button,
    FlashMessages,
    NoticeMessage,
    OrderItem,
    OrderTotals,
    ApplePayExpressCheckout,
    PromoCode,
    EmptyCart,
  },
  mixins: [applePayExpressCheckoutMixin, cartMixin],

  data() {
    return {
      merchantIdentifier: window.CHECKOUT_APPLE_PAY_MERCHANT_ID,
      scrollbarWidth: this.$store.state.page.scrollbarWidth,
      flashContext: 'mini-cart',
      ORDER_TOTALS_ROWS,
    }
  },

  computed: {
    ...mapGetters({
      overlayIsOpen: 'nav/overlayIsOpen',
      currentAdditionalNav: 'nav/currentAdditionalNav',
      deliveryNotice: 'nav/deliveryNotice',
    }),

    isMiniCartShowing() {
      return this.currentAdditionalNav === 'mini-cart'
    },

    transformStyle() {
      const transformShift = this.$store.state.nav.overlayIsOpen ? 0 : this.scrollbarWidth
      return `margin-right: -${transformShift}px;`
    },

    isBottomBoxShowing() {
      return !!this.itemsLength
    },
  },

  watch: {
    currentAdditionalNav() {
      // Helper for preventing address and bottom bar to appear/disappear when overlay is open. For mobile vps only.
      if (this.isMiniCartShowing) {
        On.initMobileScrollHelper(this.$refs.itemsContainer)
        On.initMobileScrollHelper(this.$refs.emptyContainer)
      } else {
        On.destroyMobileScrollHelper(this.$refs.itemsContainer)
        On.destroyMobileScrollHelper(this.$refs.emptyContainer)
        this.$refs.promoCode.showAddCode() // switch promo code from editing to add button mode
      }
    },

    overlayIsOpen() {
      if (!this.overlayIsOpen)
        setTimeout(() => {
          const itemsContainer = this.$refs.itemsContainer
          if (itemsContainer !== undefined) itemsContainer.scrollTop = 0
        }, On.TransitionTimes.slow)
    },
  },

  mounted() {
    onLoad().then(this.fetchData)
  },

  methods: {
    fetchData() {
      this.$store.dispatch('store/fetchData')
      this.$store.dispatch('order/fetchData')
      this.$store.dispatch('checkout/updateApplePayExpressCheckoutRequest')
    },

    closeAdditionalNav() {
      this.$store.dispatch('nav/setAdditionalNav', '')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './MiniCart';
</style>
